/**
 * Cache persistente browser-side per migliorare le performance
 * Utilizza localStorage per mantenere dati non sensibili tra sessioni
 */
import { debugWarn } from '@/utils/debugLogger';
import { safeLocalStorage } from '@/utils/safeStorage';

interface PersistentCacheData {
  data: any;
  timestamp: number;
  version: string;
  ttl: number;
}

class PersistentCache {
  private readonly CACHE_PREFIX = 'gildy_cache_';
  private readonly VERSION = '1.0.0';

  /**
   * Salva dati nella cache persistente
   */
  set(key: string, data: any, ttlHours: number = 2): void {
    if (typeof window === 'undefined') return;

    try {
      const cacheData: PersistentCacheData = {
        data,
        timestamp: Date.now(),
        version: this.VERSION,
        ttl: ttlHours * 60 * 60 * 1000
      };

      safeLocalStorage.setItem(
        this.CACHE_PREFIX + key, 
        JSON.stringify(cacheData),
        true // forceEssential - cache funzionale non-PII
      );
    } catch (error) {
      debugWarn('cache', 'Failed to save to persistent cache:', error, 'PersistentCache');
    }
  }

  /**
   * Recupera dati dalla cache persistente
   */
  get(key: string): any | null {
    if (typeof window === 'undefined') return null;

    try {
      const cached = safeLocalStorage.getItem(this.CACHE_PREFIX + key);
      if (!cached) return null;

      const cacheData: PersistentCacheData = JSON.parse(cached);
      
      // Verifica versione
      if (cacheData.version !== this.VERSION) {
        this.delete(key);
        return null;
      }

      // Verifica scadenza
      const now = Date.now();
      if (now - cacheData.timestamp > cacheData.ttl) {
        this.delete(key);
        return null;
      }

      return cacheData.data;
    } catch (error) {
      debugWarn('cache', 'Failed to read from persistent cache:', error, 'PersistentCache');
      return null;
    }
  }

  /**
   * Elimina dalla cache
   */
  delete(key: string): void {
    if (typeof window === 'undefined') return;

    try {
      safeLocalStorage.removeItem(this.CACHE_PREFIX + key);
    } catch (error) {
      debugWarn('cache', 'Failed to delete from persistent cache:', error, 'PersistentCache');
    }
  }

  /**
   * Pulisce cache scaduta
   */
  cleanup(): void {
    if (typeof window === 'undefined') return;

    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter(key => key.startsWith(this.CACHE_PREFIX));
      
      cacheKeys.forEach(key => {
        const rawKey = key.replace(this.CACHE_PREFIX, '');
        // get() farà automaticamente la pulizia se scaduto
        this.get(rawKey);
      });
    } catch (error) {
      debugWarn('cache', 'Failed to cleanup persistent cache:', error, 'PersistentCache');
    }
  }
}

export const persistentCache = new PersistentCache();